import React, { useCallback, useEffect } from "react";
import { YouTubePlayer } from "react-youtube";
import { ExpressionType, ActivityBlockConfigData, ChatConfigData, MessageConfigFromPostConfig, PostConfigData } from "../Shared/Party/PartyConfigData";
import { TitleBar, TitleBarProps } from "./TitleBar"
import { CreateFanboxMediaList, FanboxMediaList, YouTubePanel } from "./YouTubePanel";
import { ReplyPanel, ReplyPanelProps } from "./ReplyPanel";
import { LandingPage, LandingPageProps } from "./LandingPage";
import { StateManagerContext } from "./StateManagerContext";
import { StateManager } from "./PartyState";
import { ContentContextData } from "../Shared/Party/ContentContext";
import { ProfileDisplay, ProfileDisplayProps } from "./ProfileDisplay";
import { ActivityDisplay } from "./ActivityDisplay";
import { MessagePositionFunction, UIConfig, UIConfigContext, UserMessageHandler } from "./UIConfigContext";
import { MainPartyView } from "./MainPartyView";
import { UserActionDisplay } from "./UserActionDisplay";
import { UserMessageDisplay, UserMessageDisplayProps } from "./UserMessageDisplay";
import { PurchaseBoosts, PurchaseBoostsProps } from "./PurchaseBoosts";
import ManageHistoryIcon from '@mui/icons-material/ManageHistory';
import HomeIcon from '@mui/icons-material/Home';
import { CurrencyExchange, Feed, Chat, TrendingUpOutlined } from "@mui/icons-material";
import { GoogleLogin, useGoogleOneTapLogin } from "@react-oauth/google";
import { GoogleLoginHandler } from "../User/CommonAuth";
import { CreateTextLayoutEngine } from "./TextLayoutEngine";
import { maxHeight, minHeight } from "@mui/system";
import { ActionButton, ActionHandler, CreateAction, EmptyActionData } from "./ActionButton";
import { FanboxStyling } from "./CSSUtilities";



export type EventDrivenOverlayViewSubmitHandler = (data: { [key: string]: string }) => void;

export type EventDrivenOverlayViewEvent = 'reply' | 'signIn' | 'showProfile';

export type EventDrivenOverlayView = {
  name: EventDrivenOverlayViewEvent;
  create: (submit: EventDrivenOverlayViewSubmitHandler, cancel: () => void, height: number, data: { [key: string]: string }, stateManager: StateManager, uiConfig: UIConfig) => JSX.Element;
}

const eventDrivenOverlayViews: Map<EventDrivenOverlayViewEvent, EventDrivenOverlayView> = new Map();

eventDrivenOverlayViews.set('reply', {
  name: "reply",
  create: (submit, cancel, height, inputData, _stateManager, _uiConfig): JSX.Element => {
    const props: ReplyPanelProps = {
      height: height,
      replyCallback: (expression, message, boosts) => {
        const data: { [key: string]: string } = {
          expression: expression,
          message: message,
          boosts: boosts + ''
        }
        if (inputData['post'] !== undefined) {
          data.post = inputData['post'];
        }
        if (inputData['id'] !== undefined) {
          data.sourceId = inputData['id'];
        }
        submit(data);
      },
      cancelCallback: cancel

    }
    return <ReplyPanel {...props} />

  }
});

eventDrivenOverlayViews.set("signIn", {
  name: 'signIn',
  create: (_submit, cancel, height, _data, stateManager, uiConfig): JSX.Element => {
    /*const props: SignInPanelProps = {
      alreadySignedIn: cancel,
      height: height
    }*/


    const cancelHandler: ActionHandler = (action: Map<string, string>) => { cancel(); return true }

    const cancelAction = CreateAction('commentAction', [], cancelHandler);


    const cancelButtonStyling: FanboxStyling = {
      width: uiConfig.width - 40,
      height: uiConfig.fontBase * 2,
      color: {
        foreground: 'white',
        background: 'midGray'
      },
      boundary: {
        radius: 10,
      },
      font: {
        size: uiConfig.fontBase,
        weight: 'regular'
      }
    }


    return <div style={{
      minHeight: height + 'px',
      height: height + 'px',
      maxHeight: height + 'px',
      width: uiConfig.width + 'px',
      minWidth: uiConfig.width + 'px',
      maxWidth: uiConfig.width + 'px',
      backgroundColor: 'white',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems : 'center',
      position: 'relative'
    }}>
      <div style={{minHeight : '50px', maxHeight : '30px'}}></div>
      <span style={{ color: 'black', fontSize: (uiConfig.fontBase * 2) + 'px' }}>Please sign into Google to continue</span>
      <div style={{minHeight : '30px', maxHeight : '30px'}}></div>
      <GoogleLogin onSuccess={(credentials) => {
        GoogleLoginHandler(credentials, stateManager);
        cancel();
      }} />
      <div style={{minHeight : '30px', maxHeight : '30px'}}></div>
      <ActionButton styling={cancelButtonStyling} action={cancelAction} actionData={EmptyActionData} text="Back" prompt="center" />
    </div>
  }
});
eventDrivenOverlayViews.set("showProfile", {
  name: 'showProfile',
  create: (_submit, cancel, height, data): JSX.Element => {

    const props: ProfileDisplayProps = {
      height: height,
      nickname: data['nickname']
    }

    return <ProfileDisplay {...props} />
  }
});


export type MenuOverlayName = 'home' | 'actions' | 'messages' | 'posts' | 'purchase';

export type MenuOverlayView = {
  name: MenuOverlayName;
  create: (backToHome: () => void) => JSX.Element;
  icon: (size: number, onClick: () => void, key: string, selected: boolean, uiConfig: UIConfig) => JSX.Element;
}

const wrapImage = (url: string, size: number, onClick: () => void, key: string, selected: boolean) => {
  return <div
    style={{
      backgroundImage: 'url(' + url + (selected ? '_select' : '') + ')',
      backgroundSize: 'contain',
      maxHeight: size,
      minHeight: size,
      height: size,
      minWidth: size,
      width: size,
      maxWidth: size
    }} onClick={() => { onClick(); }} key={key}></div>
}

const menuOverlayView: MenuOverlayView[] = [

  {
    name: 'home',
    create: (_backToHome: () => void) => { return <div></div> },
    icon: (size: number, onClick: () => void, key: string, selected: boolean, uiConfig: UIConfig) => {

      /*const icon : Icon = <HomeIcon/>;
      icon.props.sx = { maxHeight: size, minHeight: size, height: size, marginLeft : '20px' , color : (selected ? uiConfig.fanboxColor('darkPink') : 'white') };
      icon.props.onClick = () => { onClick(); }
      icon.props.key = key;*/

      return <div style={{ backgroundColor: selected ? 'white' : uiConfig.fanboxColor('darkPink'), marginLeft: '20px' }} key={key}>
        <HomeIcon sx={{ maxHeight: size, minHeight: size, height: size, color: (selected ? uiConfig.fanboxColor('darkPink') : 'white') }} onClick={() => { onClick(); }} />

      </div>
    } //{ return wrapImage('/images/fbLogo-small.png', size, onClick, key); }
  },
  {
    name: 'actions',
    create: (_backToHome: () => void): JSX.Element => {
      return <UserActionDisplay />;
    },
    icon: (size: number, onClick: () => void, key: string, selected: boolean, uiConfig: UIConfig) => {
      return <div style={{ backgroundColor: selected ? 'white' : uiConfig.fanboxColor('darkPink'), marginLeft: '20px' }} key={key}>
        <ManageHistoryIcon style={{ maxHeight: size, minHeight: size, height: size, color: (selected ? uiConfig.fanboxColor('darkPink') : 'white') }} onClick={() => { onClick(); }} />
      </div>
    }
  },
  {
    name: 'messages',
    create: (_backToHome: () => void): JSX.Element => {
      const props: UserMessageDisplayProps = { userMessages: true };
      return <UserMessageDisplay {...props} />
    },
    icon: (size: number, onClick: () => void, key: string, selected: boolean, uiConfig: UIConfig) => {
      return <div style={{ backgroundColor: selected ? 'white' : uiConfig.fanboxColor('darkPink'), marginLeft: '20px' }} key={key}>
        <Chat style={{ maxHeight: size, minHeight: size, height: size, color: (selected ? uiConfig.fanboxColor('darkPink') : 'white') }} onClick={() => { onClick(); }} />
      </div>
    }
  },
  {
    name: 'posts',
    create: (_backToHome: () => void): JSX.Element => {
      const props: UserMessageDisplayProps = { userMessages: false };
      return <UserMessageDisplay {...props} />
    },
    icon: (size: number, onClick: () => void, key: string, selected: boolean, uiConfig: UIConfig) => {
      return <div style={{ backgroundColor: selected ? 'white' : uiConfig.fanboxColor('darkPink'), marginLeft: '20px' }} key={key}>
        <Feed style={{ maxHeight: size, minHeight: size, height: size, color: (selected ? uiConfig.fanboxColor('darkPink') : 'white') }} onClick={() => { onClick(); }} />
      </div>
    }
  },
  {
    name: 'purchase',
    create: (backToHome: () => void): JSX.Element => {
      return <PurchaseBoosts backToHome={backToHome} />
    },
    icon: (size: number, onClick: () => void, key: string, selected: boolean, uiConfig: UIConfig) => {
      return <div style={{ backgroundColor: selected ? 'white' : uiConfig.fanboxColor('darkPink'), marginLeft: '20px' }} key={key}>
        <CurrencyExchange style={{ maxHeight: size, minHeight: size, height: size, color: (selected ? uiConfig.fanboxColor('darkPink') : 'white') }} onClick={() => { onClick(); }} />
      </div>
    }
  }

]


const initUIConfig = (uiConfig: UIConfig) => {
  uiConfig.fontBase = uiConfig.height < 810 ? 14 : 16;
  uiConfig.titleHeight = 2 * uiConfig.fontBase;
  uiConfig.videoHeight = 200;
  uiConfig.videoWidth = 356;
  uiConfig.contolBackground = '#303030'
  return true;
}

type selectFunc = (index: number) => void;

export const Party = () => {

  const stateManager = React.useContext(StateManagerContext);
  const uiConfig = React.useContext(UIConfigContext);
  const _initFlag = React.useRef<boolean>(initUIConfig(uiConfig));
  const titleSelect = React.useRef<selectFunc | null>(null);
  const inDev = window.location.toString().includes('/dev');

  useGoogleOneTapLogin({
    onSuccess: (credentials) => { GoogleLoginHandler(credentials, stateManager); },
    onError: () => {
      console.log('Login Failed');
    },
  });


  const setTitleSelect = (f: (index: number) => void) => {
    titleSelect.current = f;
  }

  const height = uiConfig.height;


  //const [selectedComponent, setSelectedComponent] = React.useState("");
  const [uiStatus, setUIStatus] = React.useState<number>(0);

  const replyChatIdRef = React.useRef<string>('');
  const replyMessagePositionFunction = React.useRef<MessagePositionFunction | null>(null);



  const [showEventOverlay, setShowEventOverlay] = React.useState<boolean>(false);
  const eventOverlayRef = React.useRef<JSX.Element | null>(null);


  const ShowEventOverlay = (eventType: EventDrivenOverlayViewEvent, data: { [key: string]: string }) => {
    const eoRow = eventDrivenOverlayViews.get(eventType);
    if (eoRow !== undefined) {
      if (eventType === 'reply') {
        replyChatIdRef.current = data['id'];
        eventOverlayRef.current = eoRow.create(eventType === 'reply' ? (data) => {
          const handler = uiConfig.getUserMessageHandler();
          if (handler !== null) {
            /*var messagePosition = 1000;
            if (replyMessagePositionFunction.current !== null)
              messagePosition = replyMessagePositionFunction.current().next;*/
            const postConfig = JSON.parse(data['post']) as PostConfigData;
            const online = stateManager.activity().national;
            const timeBasis = stateManager.main().endTime - stateManager.main().startTime;
            const numMessages = Math.ceil(timeBasis / 3000) + 10;
            const optionality = Math.floor(online * 0.5);
            const impressions = online * numMessages;
            const [userMessage, chatId] = MessageConfigFromPostConfig(postConfig, optionality, 0, stateManager.user().fanbox.id, data['message'], data['expression'] as ExpressionType);
            stateManager.addUserMessage(userMessage, chatId, timeBasis, optionality, impressions);
            const worker = async () => {
              const data = {
                message: userMessage,
                chatId: chatId,
                timestamp: Date.now(),
                userId: stateManager.user().fanbox.id,
                accessToken: stateManager.user().fanbox.accessToken
              }
              const uri = '/api/user/createcomment';
              const response = await fetch(uri, {
                method: "POST",
                headers: { "content-type": "application/json" },
                body: JSON.stringify(data)
              });
              await response.json();
            }
            worker();
            handler(userMessage);
            closeEventOverlay();
          }
        } : (_data) => { closeEventOverlay(); }, closeEventOverlay, uiConfig.height - 30, data, stateManager, uiConfig);

      }
      else if (eventType === 'signIn') {
        eventOverlayRef.current = eoRow.create((_data) => { closeEventOverlay(); }, closeEventOverlay, uiConfig.height - 30, {}, stateManager, uiConfig);
      }
      else {
        eventOverlayRef.current = eoRow.create((_data) => { closeEventOverlay(); }, closeEventOverlay, uiConfig.height - 30, {}, stateManager, uiConfig);

      }
    }

    setShowEventOverlay(true);
    const panel = document.getElementById("eventOverlayDisplay");
    if (panel !== null) {
      panel.style.top = '0px';
    }

  }



uiConfig.showEventOverlay = ShowEventOverlay;



const closeEventOverlay = () => {
  const panel = document.getElementById("eventOverlayDisplay");
  if (panel !== null) {
    panel.style.top = height + 'px';
  }
  window.setTimeout(
    () => {
      setShowEventOverlay(false);
      eventOverlayRef.current = null;
    },
    1000);
}

useEffect(() => {
  uiConfig.textLayout = CreateTextLayoutEngine("TextSizingTesterDiv", "TextSizingTesterSpan")
}, [])



const TitleBarClick = (index: number) => {
  setUIStatus(index);
}

const backToHome = () => {
  if (titleSelect.current !== null)
    titleSelect.current(0);
  setUIStatus(0);
}

const setTitleBarOption = (id: MenuOverlayName) => {
  var sel = -1;
  for (let i = 0; i < menuOverlayView.length; i++) {
    if (menuOverlayView[i].name === id) {
      sel = i;
      i = menuOverlayView.length;
    }
  }
  if (sel >= 0) {
    if (titleSelect.current !== null)
      titleSelect.current(sel);
    setUIStatus(sel);
  }
}

uiConfig.showMenuItem = setTitleBarOption;



const titleBarProps: TitleBarProps = {
  onDesktop: false,
  clickOn: TitleBarClick,
  menuItems: menuOverlayView,
  selectSetter: setTitleSelect
}

return (
  <div className="scrollablePanel"
    style={{
      display: "flex",
      flexDirection: "column",
      backgroundColor: "black",
      position: "relative",
      height: uiConfig.height,
      width: uiConfig.width,
      minHeight: uiConfig.height,
      maxHeight: uiConfig.height,
      minWidth: uiConfig.width,
      maxWidth: uiConfig.width,
      overflow: 'auto'
    }}
  >
    <div style={{ position: 'absolute', visibility: 'hidden', fontFamily: 'roboto', fontWeight: 400, fontSize: '16px' }} id="TextSizingTesterDiv"><span id="TextSizingTesterSpan">This is a much longer piece of text which should be at least twice as wide or high or whatever it is that is being measured</span></div>
    <TitleBar {...titleBarProps} />
    <MainPartyView />
    <div id='eventOverlayDisplay' style={{ opacity: 1.0, transition: 'top 1s', transitionTimingFunction: 'ease-in-out', position: 'absolute', top: height + 'px' }}>
      {(showEventOverlay && eventOverlayRef.current !== null) &&
        <div style={{ display: 'flex', flexDirection: 'column', position: 'static' }}>
          <div style={{ backgroundColor: '#92007f', minHeight: '30px', maxHeight: '30px', display: 'flex', justifyContent: 'right' }} onClick={() => { closeEventOverlay(); }}><img src='/images/white-close-tranparent30.png' /></div>
          {eventOverlayRef.current}
        </div>}
    </div>

    <div style={{ position: 'absolute', top: uiConfig.titleHeight + 'px' }} >
      {menuOverlayView[uiStatus].create(backToHome)}
    </div>

  </div>

);


};
